import {
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import {
    AppStartupService,
    DateFormattingService,
    LocalWebTicket,
    ProductProperty,
    TicketProductProperty,
    useIntlPeriodTicketValidityDisplay as resolveIntlPeriodTicketValidityDisplay
} from '@ridango/core';

import { BaseTicketComponent } from '../base-ticket/base-ticket.component';
import { Temporal } from '@js-temporal/polyfill';
import Env from '@ridango/core/env';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { ProductsFacade } from '@ridango/components/lib/products';
import { filter, map, take } from 'rxjs/operators';
import { TicketValidityFacade } from '../../../../../../services/ticket-validity-facade.service';

@Component({
    selector: 'ridango-period-ticket',
    templateUrl: './period-ticket.component.html'
})
export class PeriodTicketComponent extends BaseTicketComponent implements OnInit, OnChanges {
    @Input() ticket: LocalWebTicket;
    @Input() showValidityDayName = true;
    @Input() showValidityStart = true;
    @Input() useActiveValidationText = false;
    @Input() cards = [];
    @Input() ticketsByDocNums = [];
    @Input() canDeleteFromCart = false;

    @Output() requestRemoveCard: EventEmitter<string | number> = new EventEmitter<string | number>();

    @ViewChild('ticketActionsEl') ticketActionsEl: ElementRef;

    /** Set in `refreshShowValidityAsRange` from `FORMATTING.SHOW_TICKET_AS_VALIDITY_RANGE === true` and `showValidityStart`. */
    showValidityAsRange = false;

    isActivatedAtPurchase = false;

    bookletUnitsMapping: any = {
        '=0': 'TICKET_SUMMARY_V2_UNITS_REMAINING',
        '=1': 'TICKET_SUMMARY_V2_UNITS_REMAINING',
        'other': 'TICKET_SUMMARY_V2_UNITS_REMAINING'
    };

    public validityFormatOptions = this.env.FORMATTING?.PERIOD_TICKET_VALIDITY;

    ticketValidityRange: [Temporal.Instant, Temporal.Instant];
    fixedEndTime: TicketProductProperty;
    fixedStartTime: TicketProductProperty;
    ticketValidityRangeAsString: string;
    hasFixedStartEndDates = false;
    ticketEndingAdjusted: Temporal.Instant | null = null;

    jointTicketRangeTotalDays$ = this.validityFacade.jointTicketRangeTotalDays$;

    constructor(
        @Inject('ENV') public env: Env,
        public eRef: ElementRef,
        private translate: TranslateService,
        public appStartupService: AppStartupService,
        public productsFacade: ProductsFacade,
        private dateFormattingService: DateFormattingService,
        private validityFacade: TicketValidityFacade
    ) {
        super(env, eRef, appStartupService);
    }

    ngOnInit(): void {
        super.ngOnInit();
        let properties: (ProductProperty | TicketProductProperty)[];
        const productProps = this.ticket.product?.product_properties as
            | (ProductProperty | TicketProductProperty)[]
            | undefined;

        if (productProps?.length) {
            this.applyProductProps(productProps);
        } else {
            this.productsFacade.products$
                .pipe(
                    filter((list) => Array.isArray(list) && list.length > 0),
                    map((list) => list.find((product) => product.product_id === this.ticket.product_id)),
                    filter((product): product is any => !!product),
                    take(1),
                    map((product) => (product.product_properties || []) as (ProductProperty | TicketProductProperty)[])
                )
                .subscribe((props) => this.applyProductProps(props));
        }

        if (this.ticket.product_object && this.ticket.product_object.product_properties) {
            properties = this.ticket.product_object.product_properties;
            if (properties && properties.length > 0) {
                this.isActivatedAtPurchase =
                    properties.filter((property) => property.code === 'TICKET_ACTIVATED_AT_PURCHASE')?.length > 0;
            }
        }
        this.updateTicketEndingWithOffset();
        this.refreshShowValidityAsRange();
        this.updateTicketValidityRange(this.ticket);
    }

    private refreshShowValidityAsRange(): void {
        this.showValidityAsRange =
            this.showValidityStart && this.env.FORMATTING?.SHOW_TICKET_AS_VALIDITY_RANGE !== false;
    }

    private applyProductProps(properties: (ProductProperty | TicketProductProperty)[]) {
        this.fixedEndTime = properties.find((pp) => pp.code === 'TICKET_END_TIME');
        this.fixedStartTime = properties.find((pp) => pp.code === 'TICKET_START_TIME');
    }

    ngOnChanges(changes: SimpleChanges) {
        this.refreshShowValidityAsRange();
        this.updateTicketEndingWithOffset();
        this.updateTicketValidityRange(changes.ticket?.currentValue);
    }

    get productType(): string | null {
        return (
            this.ticket?.product_type ??
            this.ticket?.product?.product_type ??
            this.ticket?.product_object?.product_type ??
            null
        );
    }

    get useIntlPeriodTicketValidityDisplay(): boolean {
        return resolveIntlPeriodTicketValidityDisplay(this.env);
    }

    get ridangoDateTimeValidityOptions() {
        return {
            format: this.env.PERIOD_TICKET_DATE_FORMAT,
            formatOptions: {
                ...this.validityFormatOptions,
                rangeSeparator: this.env.FORMATTING?.TICKET_VALIDITY_RANGE_SEPARATOR ?? ' - '
            },
            context: 'PERIOD_TICKET_VALIDITY' as const,
            applyTransportDayOffset: !!this.env.FORMATTING?.USE_TRANSPORTATION_DAY_OFFSET,
            productType: this.productType
        };
    }

    getZones() {
        if (this.ticket.start_zone_name) {
            if (this.ticket.start_zone_name !== this.ticket.end_zone_name) {
                return this.ticket.start_zone_name + ' › ' + this.ticket.end_zone_name;
            }
            return this.ticket.start_zone_name;
        }
        if (this.ticket.start_zone) {
            if (this.ticket.start_zone.name !== this.ticket.end_zone.name) {
                return this.ticket.start_zone.name + ' › ' + this.ticket.end_zone.name;
            }
            return this.ticket.start_zone.name;
        }
        if (this.ticket.area) {
            if (this.ticket.area.translations) {
                const t = this.ticket.area.translations.find((tt) => tt.lang === this.locale);
                if (t) {
                    return t.translation;
                } else if (this.ticket.area.descr) {
                    return this.ticket.area.descr;
                }
            }
            return this.ticket.area.display;
        }
        if (this.ticket.origin_stop_object) {
            if (
                this.ticket.origin_stop_object.display &&
                this.ticket.destination_stop_object.display &&
                this.ticket.origin_stop_object.display !== this.ticket.destination_stop_object.display
            ) {
                return this.ticket.origin_stop_object.display + ' › ' + this.ticket.destination_stop_object.display;
            } else if (this.ticket.origin_stop_object.display) {
                return this.ticket.origin_stop_object.display;
            }
        }

        if (this.ticket.product && this.ticket.product.name) {
            return this.ticket.product.name;
        }
        return this.translate.instant('TRAVEL_ALL_AREAS');
    }

    removeCard(subject) {
        this.requestRemoveCard.emit(subject);
    }

    updateTicketEndingWithOffset() {
        if (!this.ticket?.ending) {
            this.ticketEndingAdjusted = null;
            return;
        }
        this.ticketEndingAdjusted = this.getDisplayEndInstantFromTicketEnding(this.ticket.ending);
    }

    get cardsNotEmpty() {
        return this.cards && Object.keys(this.cards).length > 0;
    }

    updateTicketValidityRange(ticket?: LocalWebTicket) {
        if (!ticket) {
            return;
        }
        const productProperties =
            this.ticket.product_object?.product_properties || this.ticket.product?.product_properties || [];
        this.hasFixedStartEndDates = productProperties.some((prop) => prop.code === 'FIXED_START_AND_END_DATE');
        const startDateProp = productProperties.find((prop) => prop.code === 'TICKET_START_DATE');
        const endDateProp = productProperties.find((prop) => prop.code === 'TICKET_END_DATE');

        if (this.hasFixedStartEndDates) {
            this.ticketValidityRangeAsString = this.formatValidityRangeAsString(startDateProp, endDateProp);
            this.ticketValidityRange = null;
        } else if (ticket.booklet_ending && ticket.starting && (ticket.units > 1 || ticket.initial_units > 1)) {
            const start = this.dateFormattingService.parseToInstant(ticket.starting as string);
            const end = this.dateFormattingService.parseToInstant(ticket.booklet_ending as string);
            this.ticketValidityRange = [start, end];
        } else if (ticket.starting && ticket.ending) {
            this.formatStartEndingValidity(ticket);
        } else {
            this.formatDurationBasedRange(ticket);
        }
    }

    private formatStartEndingValidity(ticket?: LocalWebTicket) {
        let start: Temporal.Instant;
        if (this.env?.FORMATTING?.KEEP_ORIGINAL_STARTING_TIME_FOR_PERIOD_TICKET) {
            start = this.dateFormattingService.parseToInstant(ticket.starting as string);
        } else {
            start = Temporal.Instant.from(this.createIsoDateWith0000(ticket.starting));
        }

        const end = this.getDisplayEndInstantFromTicketEnding(ticket.ending);
        this.ticketValidityRange = [start, end];
    }

    private formatDurationBasedRange(ticket?: LocalWebTicket) {
        if (!ticket?.starting) {
            this.ticketValidityRange = null;
            return;
        }

        const DEFAULT_DURATION_DAYS = this.env.TICKET?.DEFAULT_PERIOD_TICKET_DURATION_DAYS ?? 30;
        let durationDays = DEFAULT_DURATION_DAYS;
        if (ticket.product_object?.product_properties) {
            const durationProp = ticket.product_object.product_properties.find(
                (prop) => prop.code === 'TICKET_DURATION_DAYS'
            );
            if (durationProp && !isNaN(Number(durationProp.value))) {
                durationDays = Number(durationProp.value);
            } else if (ticket.product_object?.product_length) {
                durationDays = Number(ticket.product_object.product_length);
            }
        } else if (ticket.product?.product_length) {
            durationDays = Number(ticket.product.product_length);
        }

        const endDate = this.calculateExactEndDate(ticket.starting, durationDays);
        const start = this.env?.FORMATTING?.KEEP_ORIGINAL_STARTING_TIME_FOR_PERIOD_TICKET
            ? this.dateFormattingService.parseToInstant(ticket.starting as string)
            : Temporal.Instant.from(this.createIsoDateWith0000(ticket.starting));
        const end = Temporal.Instant.from(this.createIsoDateWith2359(endDate));
        this.ticketValidityRange = [start, end];
    }

    private getDisplayEndInstantFromTicketEnding(ticketEnding: Date | string): Temporal.Instant {
        const ticketEndInstant = this.dateFormattingService.parseToInstant(ticketEnding);
        const product = this.ticket.product || this.ticket.product_object;
        const productType = product?.product_type || this.ticket.product_type;

        if (this.env?.FORMATTING?.KEEP_ORIGINAL_ENDING_TIME_FOR_PERIOD_TICKET) {
            return ticketEndInstant;
        }

        return this.dateFormattingService.adjustToTransportDayEnd(ticketEndInstant, {
            ticket: this.ticket,
            productType,
            fallbackWhenDisabled: this.createIsoDateWith2359(ticketEnding)
        });
    }

    private formatValidityRangeAsString(startProp: ProductProperty, endProp: ProductProperty): string | null {
        if (!startProp?.value || !endProp?.value) {
            return null;
        }

        const currentYear = Temporal.Now.plainDateISO().year;
        let startYear = currentYear;
        let endYear = currentYear;

        try {
            const [startMonth, startDay] = startProp.value.split('-').map(Number);
            const [endMonth, endDay] = endProp.value.split('-').map(Number);

            const currentMonth = Temporal.Now.plainDateISO().month;
            const currentDay = Temporal.Now.plainDateISO().day;

            if (startMonth > endMonth) {
                const beforeStart = currentMonth < startMonth || (currentMonth === startMonth && currentDay < startDay);
                const afterEnd = currentMonth > endMonth || (currentMonth === endMonth && currentDay > endDay);

                if (beforeStart) {
                    [startYear, endYear] = [currentYear - 1, currentYear];
                } else if (afterEnd) {
                    [startYear, endYear] = [currentYear, currentYear + 1];
                } else {
                    [startYear, endYear] = [currentYear, currentYear + 1];
                }
            } else {
                [startYear, endYear] =
                    endMonth >= currentMonth ? [currentYear - 1, currentYear] : [currentYear, currentYear + 1];
            }

            const startDate = Temporal.PlainDate.from({ year: startYear, month: startMonth, day: startDay });
            const endDate = Temporal.PlainDate.from({ year: endYear, month: endMonth, day: endDay });

            const locale = this.appStartupService.getLocale();
            const formattedStartDate = startDate.toLocaleString(locale);
            const formattedEndDate = endDate.toLocaleString(locale);

            return `${formattedStartDate} - ${formattedEndDate}`;
        } catch {
            return null;
        }
    }

    private calculateExactEndDate(startDate: Date | string, durationDays: number): Date {
        return this.appStartupService.calculateTicketEndDate(startDate, durationDays);
    }

    private createIsoDateWith2359(dateParam: Date | string): string {
        const date = dateParam instanceof Date ? dateParam : new Date(dateParam);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        const totalMinutes = parseInt(this.fixedEndTime?.value || '1439', 10);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        if (hours >= 24) {
            const timeString = '24:00:00';
            return `${year}-${month}-${day}T${timeString}${this.getTimezoneOffsetString(date)}`;
        }

        const time = Temporal.PlainTime.from({ hour: hours, minute: minutes, second: 0 });
        const timeString = time.toString({ smallestUnit: 'second', fractionalSecondDigits: 0 });

        return `${year}-${month}-${day}T${timeString}${this.getTimezoneOffsetString(date)}`;
    }

    private createIsoDateWith0000(dateParam: Date | string): string {
        const date = dateParam instanceof Date ? dateParam : new Date(dateParam);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        const totalMinutes = parseInt(this.fixedStartTime?.value || '0', 10);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const time = Temporal.PlainTime.from({ hour: hours, minute: minutes, second: 0 });
        const timeString = time.toString({ smallestUnit: 'second', fractionalSecondDigits: 0 });

        return `${year}-${month}-${day}T${timeString}${this.getTimezoneOffsetString(date)}`;
    }

    private getTimezoneOffsetString(date: Date): string {
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();

        const plainDate = Temporal.PlainDate.from({ year, month, day });
        const timeZone = Temporal.TimeZone.from(this.env.TIMEZONE || 'UTC');
        const plainTime = Temporal.PlainTime.from('00:00');

        const zdt = plainDate.toZonedDateTime({ timeZone, plainTime });
        return zdt.offset;
    }
}
